import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Btn, Input, Select, Label, Badge, Modal, Empty } from "../components/UI";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { WorkScheduleCard, AssignedClientsCard } from "./StaffPanels";
import { Users, Calendar, CheckCircle2, FileText, AlertTriangle, PenLine, ClipboardList, ListTodo, Inbox, Building2, Shield, ArrowLeft, KeyRound, Pencil, UserCheck, Clock, LogIn, Copy } from "lucide-react";
import { toast } from "sonner";

const ICONS = { users: Users, calendar: Calendar, check: CheckCircle2, file: FileText, alert: AlertTriangle, pen: PenLine, clipboard: ClipboardList, tasks: ListTodo, inbox: Inbox, building: Building2, shield: Shield };
const ROLES = [["admin", "Administrator"], ["clinician", "Clinician"], ["front_desk", "Front Desk"], ["billing", "Billing"]];
const ACTION_COLOR = { login: "#2E7D32", login_2fa: "#2E7D32", logout: "#8D6E63", create: "#1565C0", update: "#EF6C00", delete: "#C62828", sign: "#5E35B1", view: "#8D6E63", reset_password: "#C62828" };

export default function UserProfile360() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [tempPw, setTempPw] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  const load = useCallback(() => {
    api.get(`/users/${id}/profile360`).then((r) => setData(r.data)).catch((e) => setErr(apiError(e)));
    api.get("/users").then((r) => setAllUsers(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = "Staff Profile | COCOA EHR"; }, []);

  if (err) return <div className="p-8"><Empty text={err} /></div>;
  if (!data) return <div className="p-8 animate-pulse space-y-4"><div className="h-24 rounded-2xl bg-[#EFEBE9]" /><div className="h-40 rounded-2xl bg-[#EFEBE9]" /></div>;

  const u = data.user;
  const initials = `${u.first_name?.[0] || ""}${u.last_name?.[0] || ""}`;
  const openEdit = () => {
    setForm({ first_name: u.first_name, last_name: u.last_name, role: u.role, title: u.title || "", phone: u.phone || "",
      requires_cosign: !!u.requires_cosign, supervisor_id: u.supervisor_id || "" });
    setEditOpen(true);
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    try { await api.put(`/users/${id}`, form); toast.success("User updated"); setEditOpen(false); load(); }
    catch (er) { toast.error(apiError(er)); }
  };
  const resetPassword = async () => {
    try { const r = await api.post(`/users/${id}/reset-password`); setTempPw(r.data.temp_password); toast.success("Password reset"); }
    catch (er) { toast.error(apiError(er)); }
  };
  const toggleStatus = async () => {
    try { await api.put(`/users/${id}`, { status: u.status === "active" ? "inactive" : "active" }); load(); }
    catch (er) { toast.error(apiError(er)); }
  };
  const supervisors = allUsers.filter((x) => !x.requires_cosign && x.status === "active" && ["admin", "clinician"].includes(x.role) && x.id !== id);

  return (
    <div data-testid="user-profile360-page">
      <button data-testid="profile360-back-btn" onClick={() => navigate("/settings/users")} className="mb-3 flex items-center gap-1.5 text-xs font-bold text-[#7B6157] hover:text-[#3E2723]"><ArrowLeft size={13} /> All Users</button>

      <Card className="p-6 mb-5" data-testid="profile360-header">
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#4E342E] font-heading text-xl font-bold text-[#FFF8F0]">{initials}</div>
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-bold text-[#3E2723]">{u.first_name} {u.last_name}</h1>
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-[#7B6157]">
              <span className="capitalize font-semibold">{String(u.role).replace("_", " ")}</span>
              {u.title && <span>· {u.title}</span>}
              <Badge value={u.status} />
              {u.totp_enabled && <span className="rounded-full bg-[#E8F5E9] px-2 py-0.5 text-[10px] font-bold text-[#2E7D32]">2FA ON</span>}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-[#84675B]">
              <span>{u.email}</span>
              {u.phone && <span>{u.phone}</span>}
              {u.created_at && <span>Member since {dayjs(u.created_at).format("MMM YYYY")}</span>}
              {data.supervisor_name && <span className="flex items-center gap-1 text-[#B84A00] font-semibold"><UserCheck size={11} /> Supervised by {data.supervisor_name}</span>}
            </div>
          </div>
          {isAdmin && (
            <div className="ml-auto flex flex-wrap gap-2">
              <Btn variant="outline" data-testid="profile360-edit-btn" className="text-xs px-3 py-2" onClick={openEdit}><Pencil size={13} /> Edit</Btn>
              <Btn variant="outline" data-testid="profile360-reset-pw-btn" className="text-xs px-3 py-2" onClick={resetPassword}><KeyRound size={13} /> Reset Password</Btn>
              {u.id !== me.id && (
                <Btn variant="outline" data-testid="profile360-toggle-status-btn" className="text-xs px-3 py-2" onClick={toggleStatus}>{u.status === "active" ? "Deactivate" : "Activate"}</Btn>
              )}
            </div>
          )}
        </div>
        {tempPw && (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-[#FFE0B2] bg-[#FFF8E1] px-4 py-3" data-testid="profile360-temp-password">
            <KeyRound size={15} className="text-[#B84A00]" />
            <span className="text-sm text-[#3E2723]">Temporary password: <b className="font-mono">{tempPw}</b> - share securely; user should change it after login.</span>
            <button className="flex items-center gap-1 rounded-full bg-[#4E342E] px-3 py-1 text-xs font-bold text-white" onClick={() => { navigator.clipboard.writeText(tempPw); toast.success("Copied"); }}><Copy size={11} /> Copy</button>
          </div>
        )}
      </Card>

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="profile360-stats">
        {data.stats.map((s) => {
          const I = ICONS[s.icon] || FileText;
          const inner = (
            <Card key={s.label} className={`p-4 flex items-center gap-3 h-full ${s.link ? "cursor-pointer hover:shadow-md transition-shadow" : ""}`} data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <I size={18} className="text-[#7B6157] shrink-0" />
              <div><div className="font-heading text-2xl font-bold text-[#3E2723]">{s.value}</div><div className="text-[11px] font-semibold text-[#7B6157]">{s.label}</div></div>
            </Card>
          );
          return s.link ? <Link key={s.label} to={s.link}>{inner}</Link> : <div key={s.label}>{inner}</div>;
        })}
      </div>

      <div className="mb-5 grid gap-5 lg:grid-cols-2 items-start">
        <WorkScheduleCard userId={id} canEdit={isAdmin} />
        <AssignedClientsCard userId={id} canEdit={isAdmin} />
      </div>

      <div className="mb-5 grid gap-5 lg:grid-cols-2">
        <Card className="p-5" data-testid="profile360-appt-chart">
          <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-3">{u.role === "clinician" ? "My Appointments" : "Clinic Appointments"} - last 8 weeks</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.charts.appointments_by_week}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1EEE7" />
              <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#8D6E63" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#8D6E63" }} width={24} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #F1EEE7", fontSize: 12 }} />
              <Bar dataKey="appointments" fill="#8D6E63" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5" data-testid="profile360-activity-chart">
          <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-3">System Activity - last 14 days</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data.charts.activity_by_day}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1EEE7" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#8D6E63" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#8D6E63" }} width={24} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #F1EEE7", fontSize: 12 }} />
              <Area type="monotone" dataKey="events" stroke="#4E342E" fill="#D7CCC8" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="mb-5 grid gap-5 lg:grid-cols-2 items-start">
        <Card className="p-5" data-testid="profile360-upcoming">
          <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723] mb-3"><Calendar size={14} className="text-[#7B6157]" /> Upcoming Appointments</h3>
          {data.upcoming_appointments.length === 0 ? <Empty text="No upcoming appointments" /> : (
            <div className="space-y-2">
              {data.upcoming_appointments.map((a) => (
                <div key={a.id} className="flex items-center gap-3 rounded-xl border border-[#F1EEE7] px-4 py-2.5" data-testid={`upcoming-appt-${a.id}`}>
                  <div className="min-w-0">
                    <Link to={`/clients/${a.client_id}/console`} className="text-sm font-bold text-[#3E2723] hover:underline" data-testid={`upcoming-appt-client-${a.id}`}>{a.client_name}</Link>
                    <div className="text-xs text-[#7B6157]">{a.type || "Appointment"}</div>
                  </div>
                  <div className="ml-auto text-right text-xs text-[#7B6157]">
                    <div className="font-semibold text-[#3E2723]">{dayjs(a.date).format("ddd, MMM D")}</div>
                    <div>{a.start_time}{a.end_time ? ` - ${a.end_time}` : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className="p-5" data-testid="profile360-logins">
          <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723] mb-3"><LogIn size={14} className="text-[#7B6157]" /> Recent Logins</h3>
          {data.logins.length === 0 ? <Empty text="No logins recorded" /> : (
            <div className="space-y-1.5">
              {data.logins.map((l, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-[#F1EEE7] px-4 py-2 text-sm">
                  <span className="h-2 w-2 rounded-full bg-[#2E7D32]" />
                  <span className="text-[#3E2723] font-semibold">{l.action === "login_2fa" ? "Login (2FA verified)" : "Login"}</span>
                  <span className="ml-auto text-xs text-[#84675B]">{dayjs(l.timestamp).format("MMM D, YYYY h:mm A")}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Card className="p-5" data-testid="profile360-audit">
        <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723] mb-3"><Clock size={14} className="text-[#7B6157]" /> Audit & Activity Log</h3>
        {data.audit.length === 0 ? <Empty text="No activity recorded" /> : (
          <div className="max-h-96 space-y-1.5 overflow-y-auto pr-1">
            {data.audit.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-2.5 rounded-xl border border-[#F1EEE7] px-4 py-2 text-sm" data-testid={`audit-row-${l.id}`}>
                <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase text-white" style={{ background: ACTION_COLOR[l.action] || "#8D6E63" }}>{l.action}</span>
                <span className="text-[#3E2723] font-semibold capitalize">{String(l.resource || "").replace(/_/g, " ")}</span>
                {l.detail && <span className="text-xs text-[#7B6157] truncate max-w-[40%]">{l.detail}</span>}
                {l.resource === "client" && l.resource_id && <Link to={`/clients/${l.resource_id}/console`} className="text-xs font-bold text-[#6D4C41] hover:underline">open chart</Link>}
                <span className="ml-auto text-[11px] text-[#84675B]">{dayjs(l.timestamp).format("MMM D, h:mm A")}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Edit User" wide>
        {form && (
          <form onSubmit={saveEdit} className="grid grid-cols-2 gap-4">
            <div><Label>First name</Label><Input data-testid="p360-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required /></div>
            <div><Label>Last name</Label><Input data-testid="p360-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required /></div>
            <div><Label>Role</Label>
              <Select data-testid="p360-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select>
            </div>
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="col-span-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2 rounded-xl border border-[#F1E8DA] bg-[#FDFCF8] p-4">
              <label className="flex items-center gap-2 text-sm font-semibold text-[#3E2723] cursor-pointer">
                <input type="checkbox" checked={form.requires_cosign} onChange={(e) => setForm({ ...form, requires_cosign: e.target.checked, supervisor_id: e.target.checked ? form.supervisor_id : "" })} className="h-4 w-4 accent-[#4E342E]" />
                Requires co-signature
              </label>
              {form.requires_cosign && (
                <div className="mt-3"><Label>Supervising clinician</Label>
                  <Select value={form.supervisor_id} onChange={(e) => setForm({ ...form, supervisor_id: e.target.value })} required>
                    <option value="">Select supervisor…</option>
                    {supervisors.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                  </Select>
                </div>
              )}
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <Btn type="button" variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Btn>
              <Btn data-testid="p360-save-btn" type="submit">Save Changes</Btn>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
